/**
 * Einen MagicLink erstellen — `SHARE-001`, nach `04` §4.9.
 *
 * **Ein MagicLink ist kein Link, sondern ein Zugang.** `01` §1.12: „Ein
 * befristeter, widerrufbarer Zugang für Externe ohne Konto, nach Abschnitt 4.9
 * sieben Tage gültig." Nicht zu verwechseln mit der Rolle `external-partner` —
 * der Steuerberater bekommt ein eigenes Konto mit unbefristetem, aber
 * widerrufbarem Leserecht. Beides über MagicLinks abzubilden würde nach
 * `07` §7.2 die Sieben-Tage-Regel aushöhlen.
 *
 * **Was diese Ansicht baut und was ihr fehlt.** `04` §4.9 nennt sieben
 * Schritte für die Erstellung. Gebaut sind die, die eine Oberfläche leistet:
 * Ressource wählen, Empfänger angeben, Ablauf setzen, Zweck benennen. Nicht
 * gebaut sind die vier, die einen Dienst brauchen — die `share`-Prüfung beim
 * Provider, das 256-Bit-Token, der Token-Hash in der Datenbank und der Versand.
 * `SHARE-001` baut sie; der Hinweis darunter sagt es, statt es offenzulassen.
 *
 * **Sieben Tage sind die Vorgabe und nicht das Maximum ohne Grund.** Wer
 * kürzer will, kann; wer länger will, bekommt die Frage gestellt, ob er nicht
 * eigentlich einen `external-partner` meint.
 */

import { useState } from "react";

import { findeAblage, findeKunde, nachbarn } from "@hwc/demo-data";
import type { Ablageobjekt, Auftrag } from "@hwc/demo-data";
import { darfEingangBearbeiten } from "@hwc/auth";
import { Knopf, Zustand } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { erstelleLink } from "../../src/daten/vorgaenge.js";
import { Leer, Liste, Zeile, datum, groesse } from "../../src/bereiche/bausteine.js";

/** Wie lange ein Zugang gilt. `04` §4.9 setzt sieben Tage als Standard. */
const LAUFZEITEN = [
  { tage: 1, text: "1 Tag" },
  { tage: 7, text: "7 Tage (Standard)" },
  { tage: 14, text: "14 Tage" },
] as const;

export function Freigeben({ auftrag }: { auftrag: Auftrag }) {
  const { bestand, person, rollen, fuehreAus } = useSitzung();

  const [gewaehlt, setzeGewaehlt] = useState<string>("");
  const [empfaenger, setzeEmpfaenger] = useState("");
  const [tage, setzeTage] = useState(7);
  const [zweck, setzeZweck] = useState("");
  const [absage, setzeAbsage] = useState<string | null>(null);

  const kunde = findeKunde(bestand, auftrag.kunde);

  /**
   * Was sich freigeben lässt: die Ablageobjekte an diesem Auftrag.
   *
   * Nur, was über den Link-Graphen daran hängt — nicht der ganze Bestand. Wer
   * einen Zugang für einen Kunden erstellt, meint eine Sache aus dessen
   * Auftrag; eine Auswahlliste über alle Dateien des Betriebs wäre die
   * bequemste Stelle für einen Fehlgriff.
   */
  const kandidaten = nachbarn(bestand, auftrag.ref)
    .map((r) => findeAblage(bestand, r))
    .filter((o): o is Ablageobjekt => o !== undefined);

  /**
   * Die Zugänge zu diesem Auftrag.
   *
   * **Aus `bestand`, nicht aus `bestand` plus `vorgaenge`.** Der Bestand ist
   * bereits überlagert — `ueberlagere` legt neue Links hinein, bevor eine
   * Ansicht ihn sieht. Der erste Entwurf hängte die neuen zusätzlich an und
   * zeigte jeden frisch erstellten Zugang zweimal.
   *
   * Genau davor warnt der Kommentar in `daten/Sitzung.tsx`: „Die Alternative —
   * jede Ansicht fragt zusätzlich die Vorgänge ab — hat genau eine Stelle, die
   * man vergisst." Es war diese.
   */
  const bestehende = bestand.magiclinks.filter((m) =>
    kandidaten.some((k) => k.ref.opaqueId === m.ziel.opaqueId),
  );

  if (!darfEingangBearbeiten(rollen)) {
    return (
      <p className="notiz">
        Zugänge für Externe erstellt das Büro (<code>07</code> §7.2).
      </p>
    );
  }

  const erstellen = () => {
    const objekt = kandidaten.find((k) => k.ref.opaqueId === gewaehlt);
    if (!objekt) return;
    const grund = fuehreAus((v) =>
      erstelleLink({
        vorgaenge: v,
        ziel: objekt.ref,
        empfaenger: empfaenger.trim(),
        tage,
        zweck: zweck.trim() || `${objekt.name} zum Abruf`,
        akteur: person.ref,
      }),
    );
    setzeAbsage(grund ?? null);
    if (!grund) {
      setzeGewaehlt("");
      setzeEmpfaenger("");
      setzeZweck("");
    }
  };

  return (
    <>
      <h2>Zugang für Externe</h2>

      {absage && <Zustand art="warnung" text={absage} />}

      {kandidaten.length === 0 ? (
        <Leer text="An diesem Auftrag hängt nichts, das sich freigeben ließe." />
      ) : (
        <>
          <label className="feld">
            <span className="feld__name">Was</span>
            <select
              className="suchfeld"
              value={gewaehlt}
              onChange={(e) => setzeGewaehlt(e.target.value)}
            >
              <option value="">— bitte wählen —</option>
              {kandidaten.map((k) => (
                <option key={k.ref.opaqueId} value={k.ref.opaqueId}>
                  {k.name} ({groesse(k.groesseBytes)})
                </option>
              ))}
            </select>
          </label>

          <label className="feld">
            <span className="feld__name">An wen</span>
            <input
              className="suchfeld"
              type="email"
              value={empfaenger}
              onChange={(e) => setzeEmpfaenger(e.target.value)}
              placeholder={kunde?.kontakte[0]?.email ?? "empfaenger@beispiel.de"}
            />
          </label>

          <label className="feld">
            <span className="feld__name">Wie lange</span>
            <select
              className="suchfeld"
              value={tage}
              onChange={(e) => setzeTage(Number(e.target.value))}
            >
              {LAUFZEITEN.map((l) => (
                <option key={l.tage} value={l.tage}>
                  {l.text}
                </option>
              ))}
            </select>
          </label>

          <label className="feld">
            <span className="feld__name">Wofür (steht im Protokoll)</span>
            <input
              className="suchfeld"
              type="text"
              value={zweck}
              onChange={(e) => setzeZweck(e.target.value)}
              placeholder="z. B. Servicebericht zum Abruf"
            />
          </label>

          <div className="filter">
            <Knopf
              type="button"
              art="haupt"
              disabled={!gewaehlt || !empfaenger.trim()}
              onClick={erstellen}
            >
              Zugang erstellen
            </Knopf>
          </div>

          <p className="notiz">
            {/*
              Die Abgrenzung, die `07` §7.2 „wesentlich" nennt. Sie gehört an
              die Stelle, an der jemand den falschen Weg wählen würde.
            */}
            <strong>Für einen dauerhaften Zugang ist das der falsche Weg.</strong> Ein
            MagicLink läuft ab; der Steuerberater braucht ein eigenes Konto als{" "}
            <code>external-partner</code> — unbefristet, aber widerrufbar. Beides über
            MagicLinks abzubilden würde die Sieben-Tage-Regel aus <code>05</code> §5.7
            aushöhlen.
          </p>
        </>
      )}

      {bestehende.length > 0 && (
        <>
          <h3>Bestehende Zugänge</h3>
          <Liste>
            {bestehende.map((m) => (
              <Zeile
                key={m.ref.opaqueId}
                titel={m.zweck}
                unterzeile={
                  <>
                    {m.empfaenger} · {m.zugriffe}{" "}
                    {m.zugriffe === 1 ? "Zugriff" : "Zugriffe"}
                  </>
                }
                abzeichen={
                  // Auch hier führt der überlagerte Bestand das Feld bereits.
                  m.widerrufen ? (
                    <span className="abzeichen abzeichen--aus">widerrufen</span>
                  ) : (
                    <span className="abzeichen abzeichen--erfolg">gültig</span>
                  )
                }
                rechts={`bis ${datum(m.laeuftAb)}`}
              />
            ))}
          </Liste>
        </>
      )}

      <p className="notiz">
        {/*
          Was fehlt, und zwar benannt: `04` §4.9 nennt sieben Schritte, vier
          davon brauchen einen Dienst. Ein Zugang, der aussieht wie einer und
          keiner ist, wäre die gefährlichere Attrappe.
        */}
        <strong>Es entsteht noch kein echter Zugang.</strong> <code>04</code> §4.9
        verlangt die <code>share</code>-Prüfung beim Provider, ein 256-Bit-Token, dessen
        Hash allein in der Datenbank landet, und den Versand per E-Mail. Das baut{" "}
        <code>SHARE-001</code>. Hier entsteht der Eintrag, den das Portal führt —
        Empfänger, Ablauf, Zweck und die Auditspur.
      </p>
    </>
  );
}
